<template>
  <div class="flex flex-col h-full w-full max-w-full bg-gray-800 text-white overflow-hidden">
    <!-- Add Document Dialog - render outside main layout -->
    <DocumentUploader 
      v-if="showDocumentUploader"
      :chat-session-id="chatSession"
      :show-dialog="showDocumentUploader"
      @close="showDocumentUploader = false"
      @document-added="handleDocumentAdded"
      @error="handleDocumentError"
    />

    <div class="flex-1 scroll-smooth overflow-y-auto overflow-x-hidden p-4" ref="chatContainer" style="scroll-behavior: smooth;">
      <!-- Document Upload Status Indicator -->
      <div v-if="documentUploadStatus" 
           class="mb-4 px-4 py-3 rounded-lg shadow-md transition-all duration-300"
           :class="[
             documentUploadStatus === 'in_progress' ? 'bg-indigo-500/20' : 
             documentUploadStatus === 'success' ? 'bg-green-500/20' : 
             documentUploadStatus === 'error' ? 'bg-red-500/20' : ''
           ]">
        <div class="flex items-center">
          <!-- Loading Spinner -->
          <div v-if="documentUploadStatus === 'in_progress'" class="animate-spin mr-3">
            <i class="pi pi-spinner text-indigo-400"></i>
          </div>
          <!-- Success Icon -->
          <div v-else-if="documentUploadStatus === 'success'" class="mr-3">
            <i class="pi pi-check-circle text-green-400"></i>
          </div>
          <!-- Error Icon -->
          <div v-else-if="documentUploadStatus === 'error'" class="mr-3">
            <i class="pi pi-times-circle text-red-400"></i>
          </div>
          
          <!-- Status Text -->
          <div class="flex-1">
            <p v-if="documentUploadStatus === 'in_progress'" class="text-indigo-200">
              Processing document... This may take a moment.
            </p>
            <p v-else-if="documentUploadStatus === 'success'" class="text-green-200">
              Document added successfully!
            </p>
            <p v-else-if="documentUploadStatus === 'error'" class="text-red-200">
              {{ documentUploadError || 'Failed to process document. Please try again.' }}
            </p>
          </div>
          
          <!-- Close Button -->
          <button @click="documentUploadStatus = null" 
                  class="text-gray-400 hover:text-white transition-colors duration-200">
            <i class="pi pi-times"></i>
          </button>
        </div>
      </div>

      <div v-if="messages.length === 0" class="flex flex-col space-y-4">
        <p class="text-lg text-center mb-8 text-gray-300 font-light">
          Here are some example prompts to get you started:</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 px-1 sm:px-4 mx-auto w-full max-w-full">
          <div v-for="(prompt, index) in examplePrompts" :key="index" 
               class="example-prompt bg-gradient-to-br from-indigo-500 to-purple-600 p-3 rounded-lg cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
               @click="useExamplePrompt(prompt)">
            <p class="text-xs md:text-sm font-medium line-clamp-3 break-words">{{ prompt }}</p>
          </div>
        </div>
      </div>
      <div v-else class="flex flex-col space-y-4">
        <div v-for="{ id, text, isMe } in messages" :key="id" 
             :class="[
               'w-full p-4 rounded-lg shadow-md transition-all duration-300 break-words',
               isMe ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-100'
             ]">
          <div class="flex items-center mb-2">
            <div :class="[
              'h-8 w-8 rounded-full mr-3 flex items-center justify-center text-xs font-bold',
              isMe ? 'bg-indigo-400' : 'bg-gray-500'
            ]">
              {{ isMe ? 'You' : 'AI' }}
            </div>
          </div>
          <div class="text-md prose prose-invert max-w-none overflow-hidden break-words" 
               v-if="!isMe" 
               v-html="renderMarkdown(text)">
          </div>
          <p class="text-md break-words" v-else>{{ text }}</p>
          <div v-if="!isMe" class="flex justify-end mt-2 space-x-3">
            <button @click="copyToClipboard(text)" class="hover:bg-opacity-80 rounded-full transition-colors duration-200">
              <i class="pi pi-clipboard text-gray-300"></i>
            </button>
            <button @click.prevent="addToNotepad(text)" class="hover:bg-opacity-80 rounded-full transition-colors duration-200">
              <i class="pi pi-file-plus text-gray-300"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="p-4">
      <div>
        <div v-if="isReconnecting">
          Attempting to reconnect...
        </div>
        <div v-if="socketError">
          There was an error with the connection. Please try again later.
        </div>
      </div>
      <div v-if="!waiting && !isReconnecting" class="flex items-center">
        <textarea v-model="newMessage" 
          class="flex-1 bg-gray-700 rounded-lg py-2 px-3 mr-2 focus:outline-none resize-none overflow-hidden max-w-full"
          style="min-height: 40px; max-height: 120px;"
          placeholder="Enter your message..." 
          :disabled="!started" 
          @input="adjustTextareaHeight"
          @keypress.enter.prevent="sendMessage">
        </textarea>
        <button @click.prevent="sendMessage"
          class="flex-shrink-0 bg-indigo-500 hover:bg-indigo-600 rounded-full w-10 h-10 flex items-center justify-center text-white font-semibold focus:outline-none transition-colors duration-200">
          <i class="pi pi-send"></i>
        </button>
      </div>
      <div v-else class="flex items">
        <v-progress-linear :indeterminate="true"></v-progress-linear>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import { chatURL } from '@/socket';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import DocumentUploader from './DocumentUploader.vue';

export default {
  components: { DocumentUploader },
  props: {
    chatSession: {
      type: String,
      default() {
        return '';
      }
    }
  },
  data() {
    return {
      newMessage: '',
      started: false,
      waiting: true,
      socketError: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
      reconnectInterval: 3000,
      isReconnecting: false,
      messages: [],
      showDocumentUploader: false,
      examplePrompts: [
        "Summarize the key findings of this research paper.",
        "Explain the main methodology used in this study.",
        "What are the primary conclusions drawn by the authors?",
        "Compare the results of this paper with previous research in the field.",
        "Discuss the potential implications of this research for future studies.",
        "Explain this to me like I'm 5"
      ],
      currentStreamingMessage: null,
      // New properties for document upload status
      documentUploadStatus: null,  // 'in_progress', 'success', 'error'
      documentUploadError: null,
      documentJobId: null,
      statusPollInterval: null,
      pollAttempts: 0,
      maxPollAttempts: 60,  // 60 attempts of 2 seconds each (2 minutes)
    }
  },
  methods: {
    sendMessage() {
      if (this.newMessage.trim() === '') {
        return;
      }
      if (this.newMessage.length > 1000) {
        alert("Message is too long. Please keep it under 1000 characters.");
        return;
      }

      // Reset any existing streaming message
      this.currentStreamingMessage = null;

      this.messages.push({ 
        id: this.messages.length + 1, 
        text: this.newMessage, 
        isMe: true 
      });
      
      

      const data = {
        message: this.newMessage,
        user: this.$cookies.get('pb_user'),
        token: this.$cookies.get('pb_token')
      }
      const stringifiedData = JSON.stringify(data);
      const messageObj = {
        action: "sendmessage",
        data: stringifiedData
      };

      let attempt = 0;
      const maxAttempts = 3;
      while (attempt < maxAttempts) {
        try {
          if (this.socket.readyState !== WebSocket.OPEN) {
            this.startChat(this.chatSession);
          }
          this.socket.send(JSON.stringify(messageObj));
          this.waiting = true;
          this.newMessage = '';
          break;
        } catch (error) {
          console.error('Error sending message:', error);
        }
        attempt++;
      }
      // Wait for DOM update before scrolling
      this.$nextTick(() => {
        this.scrollToBottom();
      });
      if (attempt === maxAttempts) {
        alert("Failed to send message. Please try again later.");
        this.socketError = true;
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer;
        container.scrollTop = container.scrollHeight;
      });
    },
    startChat(chatSession, getOldMessages = true) {
      console.log(`Chat session: ${chatSession}`);
      const url = `${chatURL}?chatSession=${chatSession}&token=${this.$cookies.get('pb_token')}&user=${this.$cookies.get('pb_user')}`;
      console.log(url);

      this.connectWebSocket(url, chatSession, getOldMessages);
    },

    connectWebSocket(url, chatSession, getOldMessages) {
      const socket = new WebSocket(url);
      this.socket = socket;

      socket.addEventListener("open", this.handleSocketOpen);
      socket.addEventListener("message", this.handleSocketMessage);
      socket.addEventListener("close", (event) => this.handleSocketClose(event, url, chatSession));
      socket.addEventListener("error", this.handleSocketError);

      this.started = true;
      var chatDetails = this.getChatDetails(chatSession);
      console.log(chatDetails);

      if (getOldMessages) {
        this.loadChatHistory(chatSession);
      }
      this.waiting = false;
      console.log("Start chatting!");
    },

    getChatDetails(chatSession) {
      axios.get(`${process.env.VUE_APP_BASE_URL}/get-chat-details?chat_session_id=${chatSession}`,
        {
          headers: {
            'Token': `Bearer ${this.$cookies.get('pb_token')}`,
            'User': this.$cookies.get('pb_user'),
            'Content-Type': 'application/json'
          }
        })
        .then(response => {
          console.log(response.data);
        })
        .catch(error => {
          console.error('Error fetching chat details:', error);
        });
    },
    handleSocketOpen() {
      console.log("WebSocket connection established.");
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
      this.socketError = false;
    },

    handleSocketMessage(event) {
      const response = event.data;
      // Check if the response is an error message, it will be a JSON string with a message key
      if (typeof response === 'string') {
        try {
          const error = JSON.parse(response);
          if (error.message) {  
            console.error('Error message received:', error.message);
            alert(error.message);
            return;
          }
        } catch (error) {
          // If the response is not a JSON string, it's a normal response
          this.waiting = false;
        }
      }

      // If there's no current streaming message, create a new one
      if (!this.currentStreamingMessage) {
        this.currentStreamingMessage = { 
          id: this.messages.length + 1, 
          text: response, 
          isMe: false 
        };
        this.messages.push(this.currentStreamingMessage);
      } else if (!response.includes("<!!end_of_stream!!>")) {
        // Append to the existing streaming message
        this.currentStreamingMessage.text += response;
      }

      // If the response indicates it's the end of the stream, reset currentStreamingMessage
      if (response.includes("<!!end_of_stream!!>")) {
        this.currentStreamingMessage = null;
      }

      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    handleSocketClose(event, url, chatSession) {
      if (event.wasClean) {
        console.log("WebSocket connection closed cleanly:", event);
      } else {
        console.log("WebSocket connection closed uncleanly:", event);
      }

      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        this.isReconnecting = true;
        console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
        setTimeout(() => {
          this.connectWebSocket(url, chatSession, false);
        }, this.reconnectInterval);
      } else {
        console.log("Max reconnection attempts reached. Redirecting to home page.");
        this.$router.push("/");
      }
    },

    handleSocketError(event) {
      console.error("WebSocket error:", event);
      this.socketError = true;
      if (this.socket) {
        this.socket.close();
      }
    },
    simulateDisconnection() {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        console.log("Simulating WebSocket disconnection...");
        // Close the WebSocket with code 4000 to indicate it's a simulation
        this.socket.close(4000, "Simulated disconnection");
      } else {
        console.log("WebSocket is not open. Cannot simulate disconnection.");
      }
    },
    loadChatHistory(chatSession) {
      // Send a get request to `${process.env.VUE_APP_BASE_URL}/get-chat-messages?chat_session_id=chatSession`
      axios.get(`${process.env.VUE_APP_BASE_URL}/get-chat-messages?chat_session_id=${chatSession}`,
        {
          headers: {
            'Token': `Bearer ${this.$cookies.get('pb_token')}`,
            'User': this.$cookies.get('pb_user'),
            'Content-Type': 'application/json'
          }
        })
        .then(response => {
          const messages = response.data.messages.Items;
          // reverse the messages array to display the latest message at the bottom
          if (response.status !== 200) {
            alert("Invalid chat session").then(() => {
              this.$router.push("/");
            });
          }
          messages.reverse();
          messages.forEach(message => {
            this.messages.push({ id: this.messages.length + 1, text: message.message.S, isMe: message.isUser.BOOL });
          });

        }).then(() => {
          this.scrollToBottom();
        })
        .catch(error => {
          console.error('Error opening the socket:', error); // Log any errors
          this.$router.push("/");
        });
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
    },
    addToNotepad(text) {
      this.$emit('add-to-notepad', text);
    },
    useExamplePrompt(prompt) {
      this.newMessage = prompt;
      
      this.sendMessage();
    },
    renderMarkdown(text) {
      try {
        const parsed = marked(text);
        return DOMPurify.sanitize(parsed);
      } catch (error) {
        console.error('Error parsing markdown:', error);
        return text;
      }
    },
    adjustTextareaHeight(event) {
      const textarea = event.target;
      textarea.style.height = 'auto';
      const newHeight = Math.min(120, Math.max(40, textarea.scrollHeight));
      textarea.style.height = `${newHeight}px`;
    },
    
    handleDocumentAdded(documentId, jobId) {
      console.log('ChatUI: Document added to chat with ID:', documentId);
      
      if (jobId) {
        // We have a job ID, so we should track the async processing
        console.log('ChatUI: Document processing job ID:', jobId);
        this.documentJobId = jobId;
        this.documentUploadStatus = 'in_progress';
        this.pollAttempts = 0;
        this.pollJobStatus();
      } else if (documentId) {
        // No job ID but have document ID, assume success immediately
        this.documentUploadStatus = 'success';
        
        // Automatically hide success message after 5 seconds
        setTimeout(() => {
          if (this.documentUploadStatus === 'success') {
            this.documentUploadStatus = null;
          }
        }, 5000);
        
        // Add a system message to indicate a new document was added
        this.messages.push({ 
          id: this.messages.length + 1, 
          text: "📄 A new document has been added to this chat session.", 
          isMe: false 
        });
        
        // Scroll to bottom to show the new message
        this.$nextTick(() => {
          this.scrollToBottom();
        });
        
        // Emit event to parent component (PaperChat) to refresh document list
        console.log('ChatUI: Emitting document-added event to parent with ID:', documentId);
        this.$emit('document-added', documentId);
      }
    },

    handleDocumentError(errorMessage) {
      console.error('Document error:', errorMessage);
      // Update status indicator
      this.documentUploadStatus = 'error';
      this.documentUploadError = errorMessage;
      
      // Do NOT add an error message to the chat - just show in the UI status banner
    },
    
    async pollJobStatus() {
      if (this.statusPollInterval) {
        clearInterval(this.statusPollInterval);
      }
      
      this.statusPollInterval = setInterval(async () => {
        // Check if we should stop polling
        if (!this.documentJobId || this.documentUploadStatus !== 'in_progress' || this.pollAttempts >= this.maxPollAttempts) {
          clearInterval(this.statusPollInterval);
          this.statusPollInterval = null;
          
          // If we reached max attempts, show an error
          if (this.pollAttempts >= this.maxPollAttempts) {
            this.documentUploadStatus = 'error';
            this.documentUploadError = 'Document processing timed out. Please try again.';
          }
          return;
        }
        
        this.pollAttempts++;
        
        try {
          const response = await axios.get(
            `${process.env.VUE_APP_BASE_URL}/get-job-status?job_id=${this.documentJobId}`,
            {
              headers: {
                'Token': `Bearer ${this.$cookies.get('pb_token')}`,
                'User': this.$cookies.get('pb_user')}
            }
          );
          
          console.log('Job status response:', response.data);
          
          if (response.data && response.data.status) {
            if (response.data.status === 'SUCCESS') {
              // Job completed successfully
              clearInterval(this.statusPollInterval);
              this.statusPollInterval = null;
              this.documentUploadStatus = 'success';
              
              // Get the document ID from the result
              let documentId = null;
              if (response.data.result && response.data.result.document_id) {
                documentId = response.data.result.document_id;
                
                // Add success message to chat
                this.messages.push({ 
                  id: this.messages.length + 1, 
                  text: "📄 A new document has been added to this chat session.", 
                  isMe: false 
                });
                
                // Scroll to bottom to show the message
                this.$nextTick(() => {
                  this.scrollToBottom();
                });
                
                // Emit document-added event to parent component with the new document ID
                if (documentId) {
                  console.log('ChatUI: Emitting document-added event to parent with ID:', documentId);
                  this.$emit('document-added', documentId);
                }
              }
              
              // Automatically hide success message after 5 seconds
              setTimeout(() => {
                if (this.documentUploadStatus === 'success') {
                  this.documentUploadStatus = null;
                }
              }, 5000);
              
            } else if (response.data.status === 'FAILED') {
              // Job failed
              clearInterval(this.statusPollInterval);
              this.statusPollInterval = null;
              this.documentUploadStatus = 'error';
              this.documentUploadError = response.data.error || 'Failed to process document. Please try again.';
              
              // Do NOT add an error message to the chat
            }
          }
        } catch (error) {
          console.error('Error polling job status:', error);
          
          // If polling itself fails, don't stop trying unless we hit max attempts
          if (this.pollAttempts >= this.maxPollAttempts) {
            clearInterval(this.statusPollInterval);
            this.statusPollInterval = null;
            this.documentUploadStatus = 'error';
            this.documentUploadError = 'Failed to get document processing status. Please try again.';
          }
        }
      }, 2000); // Poll every 2 seconds
    },
  },
  beforeUnmount() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.close();
    }
    
    // Clear any polling interval
    if (this.statusPollInterval) {
      clearInterval(this.statusPollInterval);
      this.statusPollInterval = null;
    }
  }
}
</script>
<style>
/* Hide scrollbar track */
*::-webkit-scrollbar-track {
  background: transparent;
}

/* Style the scrollbar thumb */
*::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.3);
  /* You can adjust the transparency here */
  border-radius: 10px;
}

/* Optional: Hover styles for the thumb */
*::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.5);
  /* Adjust transparency as needed */
}

i {
  color: white;
}

.cursor-pointer {
  cursor: pointer;
}

</style>

<style scoped>
.example-prompt {
  min-height: 80px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.example-prompt:nth-child(odd) {
  background-image: linear-gradient(135deg, #667eea 0%, #764ba2 100%);


}

.example-prompt:nth-child(even) {
  background-image: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);


}

.example-prompt:hover {
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.2);
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.grid > div {
  width: 100%;
  max-width: 100%;
  margin: 0;
}

:deep(.prose) {
  color: inherit;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
}

:deep(.prose pre) {
  background-color: rgba(0, 0, 0, 0.2);
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  max-width: 100%;
  white-space: pre-wrap;
}

:deep(.prose code) {
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  word-break: break-word;
}

:deep(.prose img) {
  max-width: 100%;
  height: auto;
}

.overflow-y-auto {
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  overflow-x: hidden;
}

:deep(.prose a) {
  color: #93c5fd;
  text-decoration: underline;
  word-break: break-word;
}

:deep(.prose blockquote) {
  border-left-color: #4f46e5;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0.5rem 1rem;
  margin: 1rem 0;
  max-width: 100%;
}

:deep(.prose ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  max-width: 100%;
}

:deep(.prose ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  max-width: 100%;
}
</style>
